'use client'

import { useCallback, useEffect, useState } from 'react'
import {
  rememberNetworkUserAvatarUrl,
  resolveNetworkProfileMediaUrl,
} from '@/lib/networkProfileMediaUrl'

export interface NetworkUserProfileMedia {
  tenantId: string
  userId: string
  avatarUrl?: string | null
}

export function usePharmacyNetworkUserProfile(enabled: boolean) {
  const [profile, setProfile] = useState<NetworkUserProfileMedia | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    if (!enabled) return null
    setLoading(true)
    try {
      const res = await fetch('/api/opas/pharmacy-network/profile/me/user', {
        credentials: 'include',
        cache: 'no-store',
      })
      const data = await res.json().catch(() => ({} as Record<string, unknown>))
      if (!res.ok) return null
      const tenantId = String(data.tenantId ?? '')
      const userId = String(data.userId ?? '')
      const avatarUrl =
        resolveNetworkProfileMediaUrl((data.avatarUrl as string | null | undefined) ?? null) ?? null
      if (tenantId && userId) rememberNetworkUserAvatarUrl(tenantId, userId, avatarUrl)
      const next = { tenantId, userId, avatarUrl }
      setProfile(next)
      return next
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      setProfile(null)
      return
    }
    void load()
  }, [enabled, load])

  const updateAvatar = useCallback(
    async (patch: { avatarDataUrl?: string | null; clearAvatar?: boolean }) => {
      setSaving(true)
      try {
        const body: Record<string, unknown> = {}
        if (patch.clearAvatar) body.clearAvatar = true
        else if (patch.avatarDataUrl != null) body.avatarDataUrl = patch.avatarDataUrl

        const res = await fetch('/api/opas/pharmacy-network/profile/me/user', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error ?? 'Kaydedilemedi')

        const tenantId = String(data.tenantId ?? profile?.tenantId ?? '')
        const userId = String(data.userId ?? profile?.userId ?? '')
        const avatarUrl =
          resolveNetworkProfileMediaUrl((data.avatarUrl as string | null | undefined) ?? null) ?? null
        if (tenantId && userId) rememberNetworkUserAvatarUrl(tenantId, userId, avatarUrl)
        const next = { tenantId, userId, avatarUrl }
        setProfile(next)
        return next
      } finally {
        setSaving(false)
      }
    },
    [profile?.tenantId, profile?.userId]
  )

  return { profile, loading, saving, reload: load, updateAvatar }
}
